import React from 'react';
import { ClickAwayListener, Popper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';

import useAuthorHoverCard from '../AuthorHoverCard';
import { InitialsAvatar, POPOVER_SURFACE } from '../HoverCard';
import { LINK_BLUE, LINK_BLUE_BG, SANS_STACK } from '../common/tokens';

export function AuthorNameHover({
  author, id, name, clickable, onAuthorClick, onAuthorFilter, sx, className,
}) {
  const canClick = Boolean(clickable && onAuthorClick);
  const onCardOpen = canClick ? () => onAuthorClick(id, name) : undefined;
  const onCardFilter = id != null && onAuthorFilter
    ? () => onAuthorFilter(id, name)
    : undefined;
  const { triggerProps, popper, close } = useAuthorHoverCard(author, {
    onCardOpen,
    onCardFilter,
    placement: 'bottom-start',
  });
  const handleClick = (e) => {
    if (!canClick) return;
    e.stopPropagation();
    close();
    onAuthorClick(id, name);
  };

  return (
    <>
      <Box
        component="span"
        className={className}
        {...triggerProps}
        onClick={handleClick}
        sx={sx}
      >
        {name}
      </Box>
      {popper}
    </>
  );
}

// One row in the co-author list. Clicking navigates and closes the list;
// hovering reveals the same author hovercard the inline names use, shown to
// the side so it doesn't cover the list.
function CoAuthorRow({
  entry, onAuthorClick, onAuthorFilter, onClose,
}) {
  const clickable = entry.id != null && Boolean(onAuthorClick);
  const activate = () => {
    if (clickable) onAuthorClick(entry);
    onClose();
  };
  const onCardOpen = clickable ? activate : undefined;
  const onCardFilter = entry.id != null && onAuthorFilter
    ? () => { onAuthorFilter(entry.id, entry.name); onClose(); }
    : undefined;
  const { triggerProps, popper } = useAuthorHoverCard(entry.author, {
    onCardOpen,
    onCardFilter,
    placement: 'right-start',
  });

  return (
    <>
      <Box
        component="button"
        type="button"
        {...triggerProps}
        onClick={(e) => { e.stopPropagation(); activate(); }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          padding: '8px 10px',
          borderRadius: '6px',
          textAlign: 'left',
          cursor: 'pointer',
          border: 'none',
          background: 'transparent',
          fontFamily: SANS_STACK,
          fontSize: '0.875rem',
          color: '#1a1a1a',
          '&:hover': { background: LINK_BLUE_BG, color: LINK_BLUE },
        }}
      >
        {entry.imageUrl ? (
          <Box
            component="img"
            src={entry.imageUrl}
            alt=""
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <InitialsAvatar name={entry.name} size={24} fontSize={10} fontFamily={SANS_STACK} />
        )}
        <Box sx={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {entry.name}
        </Box>
        {entry.itemCount != null && (
          <Box sx={{ fontSize: 11, color: '#666', flexShrink: 0 }}>{entry.itemCount}</Box>
        )}
      </Box>
      {popper}
    </>
  );
}

function CoAuthorPopover({
  anchorEl, authors, onAuthorClick, onAuthorFilter, onClose,
}) {
  React.useEffect(() => {
    if (!anchorEl) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [anchorEl, onClose]);

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1500 }}
      modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box
          role="dialog"
          sx={{
            ...POPOVER_SURFACE,
            width: 244,
            maxHeight: 320,
            overflowY: 'auto',
            padding: '4px',
          }}
        >
          {authors.map((a, i) => (
            <CoAuthorRow
              key={a.id || a.name || i}
              entry={a}
              onAuthorClick={onAuthorClick}
              onAuthorFilter={onAuthorFilter}
              onClose={onClose}
            />
          ))}
        </Box>
      </ClickAwayListener>
    </Popper>
  );
}

export function AuthorHeader({
  authors, onAuthorClick, onAuthorFilter, className,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const first = authors[0];
  const rest = authors.slice(1);

  const handleChipClick = (e) => {
    e.stopPropagation();
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  const firstClickable = first && first.id != null;

  return (
    <>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        minWidth: 0,
      }}>
        <AuthorNameHover
          className={className}
          author={first.author}
          id={first.id}
          name={first.name}
          clickable={firstClickable}
          onAuthorClick={onAuthorClick}
          onAuthorFilter={onAuthorFilter}
          sx={{
            cursor: firstClickable ? 'pointer' : 'default',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            transition: 'color 0.12s',
            '&:hover': firstClickable ? { color: theme.palette.primary.main } : {},
          }}
        />
        {rest.length > 0 && (
          <Box
            component="button"
            type="button"
            aria-label={`Show ${rest.length} more authors`}
            aria-expanded={Boolean(anchorEl)}
            onClick={handleChipClick}
            sx={{
              border: '1px solid #d1d5db',
              borderRadius: '10px',
              padding: '1px 7px',
              fontSize: 10.5,
              fontWeight: 700,
              fontFamily: SANS_STACK,
              letterSpacing: '0.02em',
              lineHeight: 1.4,
              color: '#3C3C3C',
              background: '#fff',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'background 0.12s, border-color 0.12s, color 0.12s',
              '&:hover': {
                background: theme.palette.primary.tint,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              },
            }}
          >
            +{rest.length}
          </Box>
        )}
      </Box>
      {anchorEl && (
        <CoAuthorPopover
          anchorEl={anchorEl}
          authors={rest}
          onAuthorClick={(a) => {
            if (a.id != null && onAuthorClick) onAuthorClick(a.id, a.name);
          }}
          onAuthorFilter={onAuthorFilter}
          onClose={() => setAnchorEl(null)}
        />
      )}
    </>
  );
}
